import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useAudioPlayer } from '../hooks/useAudioPlayer';
import { translations } from '../lib/translations';
import type { Prosody, VoiceDemoDataType } from '../lib/types';
import { PLAY_STATE } from '../lib/types';
import {
  getDefaultVoice,
  getRandomText,
  getVoicePreferenceFromStyle,
} from '../lib/utils';

import { useVoices } from './VoicesProvider';
import { MAX_EDITOR_CONTENT_LENGTH } from '../component/voiceDemo/Editor';
import { Style } from '../component/elements/StyleJigsaw';

interface Error {
  message: string;
  type: 'rateLimit' | 'general';
}
interface VoiceDemoContextProps {
  currentVoice: VoiceDemoDataType['voices'][0];
  currentLanguage: VoiceDemoDataType;
  currentContent: string;
  currentStyle: string | null;
  prosody: Prosody;
  isPlayerLoading: boolean;
  playerState: PLAY_STATE;
  showReplay: boolean;
  audioRef: React.MutableRefObject<HTMLAudioElement | null | undefined>;
  error: Error | null;
  showUpsell: boolean;
  handleUpdateLanguage: (languageCode: string) => void;
  handleUpdateStyle: (style: Style) => void;
  handleUpdateVoice: (voiceName: string) => void;
  handleUpdateTextContent: (content: string) => void;
  handleUpdateProsody: (updatedProsody: Prosody) => void;
  togglePlayback: () => void;
  stopAndReset: () => void;
  setShowUpsell: (value: boolean) => void;
  audioDataUrl?: string;
  loadAudioDataUrl?: () => Promise<string | undefined>;
  numGeneration: number;
}

const VoiceDemoContext = createContext<VoiceDemoContextProps | undefined>(
  undefined,
);

export const VoiceDemoProvider: React.FC<{
  initialLanguage?: string;
  children: React.ReactNode;
}> = ({ initialLanguage, children }) => {
  const { voices: allVoicesData, styleText } = useVoices();
  const initialVoiceDataFromParams = initialLanguage
    ? allVoicesData.find(
        (language) =>
          language.languageName.toLowerCase() === initialLanguage.toLowerCase(),
      )
    : null;

  const defaultVoiceData = initialVoiceDataFromParams ?? allVoicesData[0];
  const currentLanguageCode = defaultVoiceData.languageCode.split(
    '-',
  )[0] as keyof typeof translations;
  const initialDemoText = getRandomText(currentLanguageCode, 0);
  const [currentVoice, setCurrentVoice] = useState(
    getDefaultVoice(defaultVoiceData.voices) ?? defaultVoiceData.voices[0],
  );
  const [currentLanguage, setCurrentLanguage] = useState(defaultVoiceData);
  const [currentContent, setCurrentContent] = useState(initialDemoText);
  const [currentStyle, setCurrentStyle] = useState<Style | null>(null);
  const [isContentEdited, setIsContentEdited] = useState(false);
  const [prosody, setProsody] = useState({ rate: 1 });
  const {
    stopAndReset,
    togglePlayback,
    playerState,
    showReplay,
    audioRef,
    error,
    setIsContentChanged,
    isContentChanged,
    showUpsell,
    setShowUpsell,
    setPlayerState,
    audioDataUrl,
    loadAudioDataUrl,
    numGeneration,
  } = useAudioPlayer(currentContent, currentStyle, prosody, currentVoice);

  const isPlayerLoading = playerState === PLAY_STATE.LOADING || !currentContent;

  const handleUpdateLanguage = (languageCode: string) => {
    const newLanguage = allVoicesData.find(
      (language) => language.languageCode === languageCode,
    );
    stopAndReset();

    if (newLanguage) {
      setCurrentLanguage(newLanguage);
      setCurrentVoice(newLanguage.voices[0]);
      const currentLanguageCode = languageCode.split(
        '-',
      )[0] as keyof typeof translations;
      const initialDemoText = getRandomText(currentLanguageCode, 0);
      setCurrentContent(initialDemoText);
    }
  };

  const getStyleText = useCallback(
    (locale: string, style: string) => {
      let text = '';
      try {
        text = styleText[locale][style];
      } catch (err) {
        console.error(err);
      }
      if (!text || text.length === 0)
        console.error(
          new Error(`empty style text found for ${locale} ${style}`),
        );
      return text ?? '';
    },
    [styleText],
  );

  const handleUpdateStyle = (style: Style) => {
    if (style === currentStyle) setCurrentStyle(null);
    else {
      setCurrentStyle(style);
      setCurrentContent(getStyleText(currentVoice.languageCode, style));
      const voice = getVoicePreferenceFromStyle(currentLanguage.voices, style);
      if (voice) setCurrentVoice(voice);
    }
    setIsContentEdited(true);
    stopAndReset();
    setIsContentChanged(true);
  };

  const handleUpdateVoice = (voiceName: string) => {
    const newVoice = currentLanguage.voices.find(
      (voice) => voice.name === voiceName,
    );
    stopAndReset();

    if (newVoice) {
      setCurrentVoice(newVoice);
      setIsContentChanged(true);
      if (isContentEdited) return;
      const currentLanguageCode = newVoice.languageCode.split(
        '-',
      )[0] as keyof typeof translations;
      const initialDemoText = getRandomText(currentLanguageCode, 0);
      setCurrentContent(initialDemoText);
    }
  };

  const handleUpdateTextContent = (content: string) => {
    setCurrentContent((current) => {
      if (current !== content) {
        setIsContentEdited(true);
        stopAndReset();
        setIsContentChanged(true);
      }
      return content;
    });
  };

  const handleUpdateProsody = (updatedProsody: Prosody) => {
    if (audioRef.current) {
      audioRef.current.playbackRate = updatedProsody.rate;
    }
    setProsody(updatedProsody);
  };

  useEffect(() => {
    if (currentContent.length > MAX_EDITOR_CONTENT_LENGTH) {
      setPlayerState(PLAY_STATE.DISABLED);
    } else if (isContentChanged) {
      togglePlayback();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [currentStyle, currentContent, currentVoice, isContentChanged]); // intentionally disabling lint because it is causing unexpected audio play/pause

  return (
    <VoiceDemoContext.Provider
      value={{
        currentVoice,
        currentLanguage,
        currentContent,
        currentStyle,
        prosody,
        isPlayerLoading,
        playerState,
        showReplay,
        audioRef,
        error,
        showUpsell,
        handleUpdateLanguage,
        handleUpdateStyle,
        handleUpdateVoice,
        handleUpdateTextContent,
        handleUpdateProsody,
        togglePlayback,
        stopAndReset,
        setShowUpsell,
        audioDataUrl,
        loadAudioDataUrl,
        numGeneration,
      }}>
      {children}
    </VoiceDemoContext.Provider>
  );
};

export const useVoiceDemo = () => {
  const context = useContext(VoiceDemoContext);
  if (!context) {
    throw new Error('useVoiceDemo must be used within a VoiceDemoProvider');
  }
  return context;
};
